// pages/Company/CompanyOnboardingPage.jsx
// ─────────────────────────────────────────────────────────────
// NEW — the screen a user lands on immediately after creating their
// FIRST company (CompanyCreatePage navigates here, not to the raw
// dashboard). Reframes "company created, now what" into a concrete
// 3-step sequence instead of dumping the user onto a dashboard with
// four equally-weighted, unordered action cards.
//
// Each step is honestly reflected as done/not-done by checking real
// data (staff count, wallet balance, payroll run history) — this is
// NOT a static checklist that stays "incomplete" forever; it
// genuinely reflects progress and should feel satisfying to clear.
//
// "Skip to dashboard" is always available and not hidden — this is
// guidance, not a forced gate. A returning/confident user can bail
// out immediately.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiClient } from "../../api/apiServices";

export default function CompanyOnboardingPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const companyName = location.state?.companyName;

  const [staffCount, setStaffCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [hasPayrollRun, setHasPayrollRun] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.get(`/mtrade/companies/${companyId}/staff`).catch(() => null),
      apiClient.get(`/mtrade/companies/${companyId}/wallet`).catch(() => null),
      apiClient.get(`/mtrade/companies/${companyId}/payroll`).catch(() => null),
    ]).then(([staffRes, walletRes, payrollRes]) => {
      if (staffRes?.data?.status) setStaffCount(staffRes.data.data.length);
      if (walletRes?.data?.status) setWalletBalance(walletRes.data.data.balance || 0);
      if (payrollRes?.data?.status) setHasPayrollRun(payrollRes.data.data.length > 0);
    }).finally(() => setLoading(false));
  }, [companyId]);

  const steps = [
    {
      key: "staff",
      done: staffCount > 0,
      icon: "ri-team-line",
      title: "Add your team",
      desc: staffCount > 0 ? `${staffCount} staff member${staffCount > 1 ? "s" : ""} added` : "Add staff one by one, or upload a CSV for bulk import",
      cta: staffCount > 0 ? "Manage staff" : "Add staff",
      onClick: () => navigate(`/business/${companyId}/staff`),
    },
    {
      key: "wallet",
      done: walletBalance > 0,
      icon: "ri-wallet-3-line",
      title: "Fund your company wallet",
      desc: walletBalance > 0 ? `₦${Number(walletBalance).toLocaleString()} available` : "Top up so you're ready to pay your team",
      cta: walletBalance > 0 ? "View wallet" : "Top up wallet",
      onClick: () => navigate("/business"),
    },
    {
      key: "payroll",
      done: hasPayrollRun,
      icon: "ri-bank-card-line",
      title: "Run your first payroll",
      desc: hasPayrollRun ? "Payroll has been run at least once" : "Review, confirm, and pay your team in a few taps",
      cta: hasPayrollRun ? "View payroll" : "Run payroll",
      // Disabled until there's both staff and wallet funds — guides
      // the user toward completing steps in a sane order without
      // hard-blocking navigation.
      disabled: staffCount === 0 || walletBalance === 0,
      onClick: () => navigate(`/business/${companyId}/payroll`),
    },
  ];

  const completedCount = steps.filter(s => s.done).length;

  return (
    <div className="min-h-screen bg-bg flex flex-col font-display">
      <div className="px-5 pt-12 pb-2 flex items-center justify-between">
        <div />
        <button onClick={() => navigate("/business")} className="text-muted text-xs font-semibold">
          Skip to dashboard →
        </button>
      </div>

      <div className="px-6 pt-4 pb-6 text-center">
        <div className="w-14 h-14 bg-success-light rounded-2xl flex items-center justify-center mx-auto mb-3">
          <i className="ri-checkbox-circle-fill text-success text-2xl" />
        </div>
        <h1 className="text-primary font-bold text-xl mb-1">
          {companyName ? `${companyName} is ready` : "Your company is ready"}
        </h1>
        <p className="text-muted text-sm">Three quick steps and you'll be ready to pay your team.</p>
      </div>

      <div className="px-5 max-w-md mx-auto w-full space-y-3 pb-10">
        {/* Progress bar — concrete, not decorative */}
        <div className="flex items-center gap-2 mb-2 px-1">
          <div className="flex-1 h-1.5 bg-surface-hover rounded-full overflow-hidden">
            <div
              className="h-full bg-brand rounded-full transition-all duration-500"
              style={{ width: `${(completedCount / steps.length) * 100}%` }}
            />
          </div>
          <span className="text-faint text-xs font-semibold flex-shrink-0">{completedCount}/{steps.length}</span>
        </div>

        {loading ? (
          [1, 2, 3].map(i => <div key={i} className="h-24 bg-skeleton rounded-2xl animate-pulse" />)
        ) : (
          steps.map((step, i) => (
            <button
              key={step.key}
              onClick={step.disabled ? undefined : step.onClick}
              disabled={step.disabled}
              className="w-full bg-surface border rounded-2xl p-4 text-left transition-all flex items-center gap-4 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: step.done ? "var(--color-success-border)" : "var(--color-border)" }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: step.done ? "var(--color-success-light)" : "var(--color-brand-light)" }}
              >
                <i className={step.done ? "ri-check-line" : step.icon} style={{ color: step.done ? "var(--color-success)" : "var(--color-brand)" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-primary font-semibold text-sm">{step.title}</p>
                <p className="text-muted text-xs mt-0.5">{step.desc}</p>
              </div>
              {!step.done && !step.disabled && (
                <i className="ri-arrow-right-s-line text-faint text-xl flex-shrink-0" />
              )}
            </button>
          ))
        )}

        {completedCount === steps.length && (
          <button
            onClick={() => navigate("/business")}
            className="w-full bg-brand hover:bg-brand-hover text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-[0.98] mt-2"
          >
            Go to Dashboard
          </button>
        )}
      </div>
    </div>
  );
}
